import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import Stepper from "./Stepper";
import StepCv from "./StepCv";
import StepJob from "./StepJob";
import StepCompat from "./StepCompat";
import StepImprove from "./StepImprove";
import StepDownload from "./StepDownload";
import StepTrack from "./StepTrack";
import TailorProgress from "./TailorProgress";
import KeshTip from "./KeshTip";
import { KESH_HELP, keshContext, stepLabel } from "./constants";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Studio({ appId, baseCv, onCvChange, onChanged, onOpenApp, onExit }) {
  const [app, setApp] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [loading, setLoading] = useState(Boolean(appId));
  const [step, setStep] = useState(baseCv ? 2 : 1);
  const run = useRef(0);
  useEffect(() => () => keshContext(null), []);

  const poll = useCallback(async (id, my) => {
    const started = Date.now();
    while (run.current === my && Date.now() - started < 360000) {
      await sleep(3000);
      if (run.current !== my) return;
      try {
        const { data } = await api.get(`/applications/${id}`);
        if (run.current !== my) return;
        if (data.processing_status === "done") {
          setProcessing(null); setApp(data); setStep(3); onChanged?.();
          toast.success(`Done! Your job match is ${data.ats_score}%.`);
          return;
        }
        if (data.processing_status === "failed") {
          setProcessing(null); setStep(2);
          toast.error("Tailoring didn't finish this time. Please try again; your CV is unchanged.");
          return;
        }
      } catch { /* keep polling through brief network blips */ }
    }
  }, [onChanged]);

  useEffect(() => {
    const my = ++run.current;
    setProcessing(null);
    if (!appId) { setApp(null); setStep(baseCv ? 2 : 1); setLoading(false); return () => { run.current++; }; }
    setLoading(true);
    api.get(`/applications/${appId}`)
      .then(({ data }) => {
        if (run.current !== my) return;
        if (data.processing_status === "processing") { setApp(null); setProcessing(data); poll(data.id, my); }
        else if (data.processing_status === "failed") { setApp(null); setStep(2); toast.error("That application didn't finish. Please try again."); }
        else { setApp(data); setStep(data.journey_step || 3); }
      })
      .catch(() => { if (run.current === my) { toast.error("We couldn't open that application."); onOpenApp(null); } })
      .finally(() => { if (run.current === my) setLoading(false); });
    return () => { run.current++; }; // eslint-disable-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const shown = processing ? 2 : step;
  useEffect(() => {
    const where = app ? ` (${app.role || "role"} at ${app.company || "company"})` : "";
    keshContext({ inline: true, page: `CV Studio, step ${shown} of 6: ${stepLabel(shown)}${where}`.slice(0, 190), appId: app?.id, suggestions: KESH_HELP[shown]?.ask });
  }, [shown, app?.id, app?.role, app?.company]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (n) => {
    setStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (app && n >= 3) api.patch(`/applications/${app.id}/progress`, { step: n }).catch(() => {});
  };
  const update = (data) => {
    const clean = Object.fromEntries(Object.entries(data || {}).filter(([, v]) => v !== undefined && v !== null));
    setApp((a) => ({ ...a, ...clean }));
    onChanged?.();
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 text-brand animate-spin" aria-label="Loading" /></div>;

  const shared = { app, onUpdate: update };
  return (
    <div data-testid="cv-studio" className="min-w-0">
      <div className="flex items-center justify-between gap-3 mb-4 sm:mb-5">
        <div className="gi-eyebrow min-w-0 truncate" data-testid="studio-title">CV Studio{app ? ` · ${app.role || "Application"}${app.company ? ` at ${app.company}` : ""}` : ""}</div>
        {(app || processing) && (
          <button data-testid="studio-new-button" onClick={() => onOpenApp(null)} className="gi-btn gi-btn-tertiary gi-btn-sm shrink-0">
            <Plus className="w-4 h-4" /> <span className="hidden min-[420px]:inline">New application</span><span className="min-[420px]:hidden">New</span>
          </button>
        )}
      </div>
      <Stepper step={shown} maxStep={app ? 6 : baseCv ? 2 : 1} onGo={go} />
      <KeshTip step={shown} />
      {processing ? (
        <>
          <TailorProgress role={processing.role} />
          <button onClick={() => onExit("home")} data-testid="tailor-leave-button" className="mt-4 gi-btn gi-btn-secondary">Go to Home while I wait</button>
        </>
      ) : step === 1 ? (
        <StepCv baseCv={baseCv} locked={Boolean(app)} onCvChange={onCvChange} onNext={() => go(2)} />
      ) : step === 2 ? (
        <StepJob app={app} onBack={() => go(1)} onNext={() => go(3)} onNew={() => onOpenApp(null)} onStart={(d) => onOpenApp(d.id)} />
      ) : !app ? null : step === 3 ? (
        <StepCompat {...shared} onBack={() => go(2)} onNext={() => go(4)} />
      ) : step === 4 ? (
        <StepImprove {...shared} onBack={() => go(3)} onNext={() => go(5)} />
      ) : step === 5 ? (
        <StepDownload {...shared} onBack={() => go(4)} onNext={() => go(6)} />
      ) : (
        <StepTrack {...shared} onBack={() => go(5)} onFinish={() => onExit("tracker")} />
      )}
    </div>
  );
}
